import React, { useState } from 'react';
import { Bed } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { RoomDetailsModal } from './RoomDetailsModal';

type Props = {
  number: string;
  type: 'd' | 't' | 'q';
  occupied: number;
  total: number;
  pilgrims: Array<{
    id: string;
    name: string;
    checkIn: number;
    checkOut: number;
    gender: 'M' | 'F';
    guideName: string;
    organizerName: string;
    age: number;
    guideId: string;
    organizerId: string;
  }>;
};

export const Room = ({
  number,
  type,
  occupied,
  total,
  pilgrims = [],
}: Props) => {
  const { translations } = useLanguage();
  const [showDetails, setShowDetails] = useState(false);
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  const getTypeColor = () => {
    switch (type) {
      case 'd':
        return 'bg-blue-50 border-blue-200';
      case 't':
        return 'bg-green-50 border-green-200';
      case 'q':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getOccupancyColor = () => {
    // Calculate actual occupancy based on current assignments
    const currentOccupancy = pilgrims.length;
    const occupancyRate = (currentOccupancy / total) * 100;

    if (occupancyRate >= 100) return 'bg-red-500';
    if (occupancyRate > 0) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Get room type label dynamically
  const getRoomTypeLabel = (type: string) => {
    return (
      translations?.room?.roomTypes?.[type] ||
      {
        d: 'Double',
        t: 'Triple',
        q: 'Quadruple',
      }[type] ||
      type
    );
  };

  // Check if user has access to pilgrim details
  const canViewPilgrimDetails = (pilgrim: any) => {
    if (userRole === 'admin') return true;
    if (userRole === 'guide') return pilgrim.guideId === userId;
    if (userRole === 'organizer') return pilgrim.organizerId === userId;
    return false;
  };

  // Group pilgrims by gender
  const maleCount = pilgrims.filter(p => p.gender === 'M').length;
  const femaleCount = pilgrims.filter(p => p.gender === 'F').length;

  // Check if user has any pilgrims in this room
  const hasUserPilgrims = pilgrims.some(pilgrim => {
    if (userRole === 'guide') return pilgrim.guideId === userId;
    if (userRole === 'organizer') return pilgrim.organizerId === userId;
    return false;
  });

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.05 }}
        onClick={() => setShowDetails(true)}
        className={`
          relative p-2.5 rounded-lg border-2 transition-all h-full
          ${getTypeColor()}
          cursor-pointer min-w-[100px] max-w-full
          ${hasUserPilgrims ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
        `}
      >
        {/* Room Number */}
        <div className="text-sm font-medium mb-1 truncate">{number}</div>

        {/* Room Type */}
        <div className="flex items-center gap-1 mb-1">
          <Bed size={14} className="text-gray-500 shrink-0" />
          <span className="text-xs text-gray-600 truncate">
            {getRoomTypeLabel(type)}
          </span>
        </div>

        {/* Occupancy */}
        <div className="flex items-center gap-1">
          <div className="flex-1 min-w-0">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span className="truncate">
                {pilgrims.length}/{total}
              </span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getOccupancyColor()}`}
                style={{ width: `${(pilgrims.length / total) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Gender Distribution */}
        {pilgrims.length > 0 && (
          <div className="mt-2 flex gap-1">
            {maleCount > 0 && (
              <div className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                {maleCount} M
              </div>
            )}
            {femaleCount > 0 && (
              <div className="text-xs px-1.5 py-0.5 rounded bg-pink-100 text-pink-700">
                {femaleCount} F
              </div>
            )}
          </div>
        )}

        {/* Pilgrim Indicators */}
        {pilgrims.length > 0 && (
          <div className="absolute top-2 left-2 flex -space-x-1">
            {pilgrims.map((pilgrim, index) => (
              <div
                key={`${pilgrim.id}-${index}`}
                className={`w-2 h-2 rounded-full border border-white ${
                  pilgrim.gender === 'M' ? 'bg-blue-400' : 'bg-pink-400'
                } ${canViewPilgrimDetails(pilgrim) ? 'animate-pulse' : ''}`}
                title={canViewPilgrimDetails(pilgrim) ? pilgrim.name : 'Pilgrim'}
              />
            ))}
          </div>
        )}

        {/* User's Pilgrims Indicator */}
        {hasUserPilgrims && (
          <div 
            className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full border border-white shadow-lg"
            style={{
              animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
              boxShadow: '0 0 0 0 rgba(0, 137, 123, 0.7)'
            }}
          >
            <style>
              {`
                @keyframes ping {
                  75%, 100% {
                    transform: scale(2);
                    opacity: 0;
                  }
                }
              `}
            </style>
          </div>
        )}
      </motion.div>

      {/* Room Details Modal */}
      <RoomDetailsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        details={{
          roomNumber: number,
          type,
          occupied: pilgrims.length,
          total,
          pilgrims: pilgrims.map(p => ({
            ...p,
            name: canViewPilgrimDetails(p) ? p.name : '****',
            guideName: canViewPilgrimDetails(p) ? p.guideName : '****',
            organizerName: canViewPilgrimDetails(p) ? p.organizerName : '****'
          }))
        }}
      />
    </>
  );
};